import { useMutation } from "@tanstack/react-query";
import { MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { useTranslations } from "use-intl";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@lawline/ui/components/alert-dialog";
import { Button } from "@lawline/ui/components/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@lawline/ui/components/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@lawline/ui/components/tooltip";

import { useAuth } from "@platform/auth/context";
import { revokeKeyMutation } from "../api-keys.mutations";
import type { ApiKey } from "../api-keys.types";
import { effectiveStatus } from "../api-keys.utils";

// Renders nothing when the viewer has no action, because of a missing permission
// or an inactive key. Revocation is permanent, so an alert dialog confirms it.
export function KeyActions({ apiKey }: { apiKey: ApiKey }) {
	const t = useTranslations("apiKeys.actions");
	const tActions = useTranslations("common.actions");
	const { can } = useAuth();
	const revoke = useMutation(revokeKeyMutation);
	const [revokeOpen, setRevokeOpen] = useState(false);

	const canRevoke = effectiveStatus(apiKey) === "active" && can({ apiKey: ["revoke"] });
	if (!canRevoke) return null;

	return (
		<>
			<DropdownMenu>
				<Tooltip>
					<TooltipTrigger asChild>
						<DropdownMenuTrigger asChild>
							<Button variant="ghost" size="icon-xs">
								<MoreHorizontal />
							</Button>
						</DropdownMenuTrigger>
					</TooltipTrigger>
					<TooltipContent>{tActions("more")}</TooltipContent>
				</Tooltip>
				<DropdownMenuContent align="end">
					<DropdownMenuItem variant="destructive" onClick={() => setRevokeOpen(true)}>
						{t("revoke")}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>

			<AlertDialog open={revokeOpen} onOpenChange={setRevokeOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t("revokeConfirmTitle", {
								keyName: apiKey.name ?? t("revokeConfirmDefaultKeyName"),
							})}
						</AlertDialogTitle>
						<AlertDialogDescription>{t("revokeConfirmDescription")}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("revokeConfirmCancel")}</AlertDialogCancel>
						<AlertDialogAction variant="destructive" onClick={() => revoke.mutate(apiKey.id)}>
							{t("revokeConfirmConfirm")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
}
